import {
  ISeriesPrimitive,
  SeriesAttachedParameter,
  Time,
  IPrimitivePaneRenderer,
  IPrimitivePaneView,
  PrimitivePaneViewZOrder,
} from 'lightweight-charts';
import {
  LAYOUT_PRIORITY,
  MarkerLayoutClient,
  type LayoutContext,
  type LayoutItem,
  type MarkerLayoutRegistry,
  type PlacedItem,
} from './markerLayout';

export interface ConfluenceDetailData {
  symbol: string;
  points: number;
  confidence?: number; // Signal confidence score from the correlated symbol
  skipped?: boolean; // Whether the correlated signal was skipped
}

export interface SignalMarkerData {
  time: number; // timestamp in ms
  price: number; // candle high or low
  isBullish: boolean; // determines if text is below (bullish) or above (bearish) the candle
  confidence: number; // confidence score 0-100
  confluence?: number; // confluence score (can be negative)
  confluenceSymbols?: string[]; // symbols that contributed to confluence
  confluenceDetails?: ConfluenceDetailData[]; // detailed per-symbol confluence data
  primarySymbol?: string; // the marker's own symbol (for cross-index detection)
  takeItThreshold?: number; // threshold for "TAKE IT" indicator (default: 80)
  takeItEnabled?: boolean; // whether to show "TAKE IT" indicator (default: true)
  mlScore?: number; // ML model probability score 0-100
  mlRecommendation?: 'TAKE' | 'SKIP'; // ML model recommendation
  // Profitability data (rendered at end of stack, farthest from candle)
  isProfitable?: boolean;
  pnl?: number;
  maePercent?: number; // MAE as percentage of stop loss (0-100+)
}

// Candle bounds for collision detection with candle bodies/wicks
export interface CandleBounds {
  time: number; // timestamp in ms
  x: number; // x coordinate in pixels
  highY: number; // y coordinate of high (wick top)
  lowY: number; // y coordinate of low (wick bottom)
  openY: number; // y coordinate of open
  closeY: number; // y coordinate of close
  bodyTop: number; // y coordinate of body top (min of open, close)
  bodyBottom: number; // y coordinate of body bottom (max of open, close)
  width: number; // candle width in pixels
}

interface SignalMarkerRendererData {
  x: number;
  y: number;
  isBullish: boolean;
  confidence: number;
  confluence?: number;
  confluenceSymbols?: string[];
  confluenceDetails?: ConfluenceDetailData[];
  primarySymbol?: string;
  takeItThreshold?: number;
  takeItEnabled?: boolean;
  mlScore?: number;
  mlRecommendation?: 'TAKE' | 'SKIP';
  // Profitability data
  isProfitable?: boolean;
  pnl?: number;
  maePercent?: number;
  // Collision detection fields
  isDisplaced: boolean;
  displacementAmount: number;
  anchorY: number; // Original Y position for leader line
  anchorX: number; // Original X position for horizontal displacement
  priceY: number; // Candle price Y coordinate (for drawing marker dot)
  markerTime: number; // Original marker timestamp in ms (for click-to-expand tracking)
  stackHeight: number; // Estimated height of this marker's text stack
  stackWidth: number; // Estimated width of this marker's text (for accurate collision)
  flatMode: boolean; // Use flat 2-line layout when collision detection is ON
  // Advanced collision fields
  priority: number; // Higher priority markers stay in place (based on confidence, ML, confluence)
  horizontalOffset: number; // Horizontal micro-offset for dense clusters
  layer: number; // Layer assignment for bin packing algorithm
  textHidden?: boolean; // shared layout folded the text box into a "+N" chip
}

/** Dot centre distance from the candle extreme, px (matches the renderer's dotOffset). */
const DOT_OFFSET = 12;
/** Layout box of the dot and its ring (ring radius 7 + stroke + glow). */
const DOT_BOX = 16;


// Collision detection uses a simple layered algorithm with hardcoded optimal values

// Helper to detect cross-index (NQ/MNQ family vs ES/MES family)
function isCrossIndex(primarySymbol: string, correlatedSymbol: string): boolean {
  const isNqFamily = (s: string) => s === 'NQ' || s === 'MNQ';
  const isEsFamily = (s: string) => s === 'ES' || s === 'MES';

  // Extract base symbol (NQ from NQH6.CME, MNQ from MNQH6.CME, etc)
  const extractBase = (sym: string): string => {
    const upper = sym.toUpperCase();
    if (upper.startsWith('MNQ')) return 'MNQ';
    if (upper.startsWith('MES')) return 'MES';
    if (upper.startsWith('NQ')) return 'NQ';
    if (upper.startsWith('ES')) return 'ES';
    return sym;
  };

  const primary = extractBase(primarySymbol);
  const correlated = extractBase(correlatedSymbol);

  return (isNqFamily(primary) && isEsFamily(correlated)) ||
         (isEsFamily(primary) && isNqFamily(correlated));
}

class SignalMarkerPaneRenderer implements IPrimitivePaneRenderer {
  private _data: SignalMarkerRendererData[] = [];
  private _fontSize: number = 11;
  private _lineHeightMultiplier: number = 1.2;
  private _boxPadding: number = 5;
  private _showTextOverlay: boolean = true;
  private _expandedMarkers: Set<number> = new Set();
  private _recentMarkers: Set<number> = new Set();
  private _onCirclePositions: ((positions: { time: number; x: number; y: number; radius: number }[]) => void) | null = null;

  update(
    data: SignalMarkerRendererData[],
    fontSize: number,
    lineHeightMultiplier: number,
    boxPadding: number,
    showTextOverlay: boolean,
    expandedMarkers: Set<number>,
    recentMarkers: Set<number>,
    onCirclePositions: (positions: { time: number; x: number; y: number; radius: number }[]) => void
  ): void {
    this._data = data;
    this._fontSize = fontSize;
    this._lineHeightMultiplier = lineHeightMultiplier;
    this._boxPadding = boxPadding;
    this._showTextOverlay = showTextOverlay;
    this._expandedMarkers = expandedMarkers;
    this._recentMarkers = recentMarkers;
    this._onCirclePositions = onCirclePositions;
  }

  // Check if text should be shown for a specific marker
  private _shouldShowText(markerTime: number): boolean {
    if (this._showTextOverlay) return true;
    if (this._expandedMarkers.has(markerTime)) return true;
    if (this._recentMarkers.has(markerTime)) return true;
    return false;
  }

  draw(target: any): void {
    target.useBitmapCoordinateSpace((scope: any) => {
      const ctx = scope.context as CanvasRenderingContext2D;
      const pixelRatio = scope.horizontalPixelRatio as number;
      const scaledFontSize = this._fontSize * pixelRatio;
      const lineHeight = scaledFontSize * this._lineHeightMultiplier;

      ctx.font = `${scaledFontSize}px Arial`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // First pass: draw leader lines from circle dot to text box (when text overlay is visible)
      if (this._showTextOverlay) {
        for (const marker of this._data) {
          if (marker.textHidden || !this._shouldShowText(marker.markerTime)) continue;
          const anchorX = marker.anchorX * pixelRatio;
          const x = (marker.x + (marker.horizontalOffset || 0)) * pixelRatio;
          const dotYOffset = 12 * pixelRatio; // Must match dotOffset in circle drawing
          const anchorY = marker.priceY * pixelRatio + (marker.isBullish ? dotYOffset : -dotYOffset);
          const textY = (marker.y + marker.displacementAmount) * pixelRatio;

          // Only draw if there's meaningful vertical distance between dot and text
          const distance = Math.abs(textY - anchorY);
          if (distance > 8 * pixelRatio) {
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
            ctx.lineWidth = 1 * pixelRatio;
            ctx.setLineDash([2 * pixelRatio, 2 * pixelRatio]);
            ctx.beginPath();
            ctx.moveTo(anchorX, anchorY);
            ctx.lineTo(x, textY);
            ctx.stroke();
            ctx.setLineDash([]);
          }
        }
      }

      // Second pass: draw marker dots with colored rings near candle price
      const dotOffset = 12 * pixelRatio; // Offset from candle high/low
      const circlePositions: { time: number; x: number; y: number; radius: number }[] = [];

      for (const marker of this._data) {
        const dotX = marker.anchorX * pixelRatio;
        // Apply offset: bullish below candle low, bearish above candle high
        const dotY = marker.priceY * pixelRatio + (marker.isBullish ? dotOffset : -dotOffset);
        const dotRadius = 4 * pixelRatio;
        const ringRadius = 7 * pixelRatio;

        // Track circle position for click detection (in CSS pixels, not scaled)
        circlePositions.push({
          time: marker.markerTime,
          x: marker.anchorX,
          y: marker.priceY + (marker.isBullish ? dotOffset / pixelRatio : -dotOffset / pixelRatio),
          radius: ringRadius / pixelRatio,
        });

        // Calculate max confluence confidence for ring color (same logic as box border)
        const symbolSkippedMap = new Map<string, boolean>();
        const symbolConfidenceMap = new Map<string, number>();
        if (marker.confluenceDetails) {
          for (const detail of marker.confluenceDetails) {
            const baseSym = detail.symbol.replace(/[A-Z]\d+\..*$/, '').replace(/\..*$/, '');
            if (detail.skipped !== undefined) {
              symbolSkippedMap.set(baseSym, detail.skipped);
            }
            if (detail.confidence !== undefined) {
              symbolConfidenceMap.set(baseSym, detail.confidence);
            }
          }
        }

        const symbols = marker.confluenceSymbols ||
          (marker.confluenceDetails?.map(d => d.symbol.replace(/[A-Z]\d+\..*$/, '').replace(/\..*$/, '')) ?? []);

        const nonSkippedConfidences = symbols
          .filter(sym => !symbolSkippedMap.get(sym))
          .map(sym => symbolConfidenceMap.get(sym) ?? 0);
        const maxConfidence = nonSkippedConfidences.length > 0
          ? Math.max(...nonSkippedConfidences)
          : 0;

        // Determine ring color and glow based on max confluence confidence
        let ringColor: string;
        let glowColor: string | null = null;
        let ringWidth = 2 * pixelRatio;

        if (maxConfidence >= 75) {
          ringColor = '#fbbf24'; // Gold
          glowColor = '#fbbf24';
          ringWidth = 2.5 * pixelRatio;
        } else if (maxConfidence >= 65) {
          ringColor = '#22c55e'; // Green
          glowColor = '#22c55e';
        } else {
          ringColor = 'rgba(100, 116, 139, 0.7)'; // Gray
        }

        // Draw filled circle (inner dot)
        const fillColor = marker.isBullish ? '#26a69a' : '#ef5350';
        ctx.beginPath();
        ctx.arc(dotX, dotY, dotRadius, 0, Math.PI * 2);
        ctx.fillStyle = fillColor;
        ctx.fill();

        // Draw colored ring with optional glow
        ctx.save();
        if (glowColor) {
          ctx.shadowColor = glowColor;
          ctx.shadowBlur = 6 * pixelRatio;
        }
        ctx.beginPath();
        ctx.arc(dotX, dotY, ringRadius, 0, Math.PI * 2);
        ctx.strokeStyle = ringColor;
        ctx.lineWidth = ringWidth;
        ctx.stroke();
        ctx.restore();
      }

      // Report circle positions for click detection
      this._onCirclePositions?.(circlePositions);

      // Third pass: draw text (per-marker visibility check)
      for (const marker of this._data) {
        // Skip text if this marker shouldn't show it
        if (marker.textHidden || !this._shouldShowText(marker.markerTime)) continue;
        // Apply horizontal offset for dense clusters
        const x = (marker.x + (marker.horizontalOffset || 0)) * pixelRatio;
        // Apply displacement if marker was displaced due to collision
        const y = (marker.y + marker.displacementAmount) * pixelRatio;

        // Color based on confidence level
        let confidenceColor: string;
        if (marker.confidence >= 70) {
          confidenceColor = '#00ff00'; // High confidence - green
        } else if (marker.confidence >= 50) {
          confidenceColor = '#ffff00'; // Medium confidence - yellow
        } else {
          confidenceColor = '#ff6666'; // Low confidence - red
        }

        const confText = `${Math.round(marker.confidence)}`;

        // ML scoring deprecated - always false (keep variables for code paths)
        const hasML = false;
        const mlText = '';
        const mlColor = '#888888';

        // Check if "TAKE IT" indicator should be shown
        const takeItEnabled = marker.takeItEnabled !== false; // default true
        const takeItThreshold = marker.takeItThreshold ?? 80;
        const combinedScore = marker.confidence + (marker.confluence ?? 0);
        const showTakeIt = takeItEnabled && combinedScore > takeItThreshold;

        // Confluence data
        const hasConfluence = marker.confluence !== undefined && marker.confluence !== 0;
        let confluenceColor = '#86efac';
        if (hasConfluence) {
          if (marker.confluence! >= 50) {
            confluenceColor = '#fbbf24'; // Gold for high positive
          } else if (marker.confluence! > 0) {
            confluenceColor = '#86efac'; // Light green for low positive
          } else {
            confluenceColor = '#f87171'; // Red for negative
          }
        }
        const cfText = hasConfluence ? `${marker.confluence! >= 0 ? '+' : ''}${Math.round(marker.confluence!)}` : '';
        const primarySymbol = marker.primarySymbol || '';

        // Build lookup maps from symbol to points, confidence, and skipped using confluenceDetails
        const symbolPointsMap = new Map<string, number>();
        const symbolConfidenceMap = new Map<string, number>();
        const symbolSkippedMap = new Map<string, boolean>();
        const derivedSymbols: string[] = [];
        if (marker.confluenceDetails) {
          for (const detail of marker.confluenceDetails) {
            // Extract base symbol (e.g., "NQ" from "NQH6.CME")
            const baseSym = detail.symbol.replace(/[A-Z]\d+\..*$/, '').replace(/\..*$/, '');
            derivedSymbols.push(baseSym);
            symbolPointsMap.set(baseSym, detail.points);
            symbolPointsMap.set(detail.symbol, detail.points);
            if (detail.confidence !== undefined) {
              symbolConfidenceMap.set(baseSym, detail.confidence);
              symbolConfidenceMap.set(detail.symbol, detail.confidence);
            }
            if (detail.skipped !== undefined) {
              symbolSkippedMap.set(baseSym, detail.skipped);
              symbolSkippedMap.set(detail.symbol, detail.skipped);
            }
          }
        }
        // Use confluenceSymbols if available, otherwise derive from confluenceDetails
        const symbols = (marker.confluenceSymbols && marker.confluenceSymbols.length > 0)
          ? marker.confluenceSymbols
          : derivedSymbols;

        // ============ FLAT MODE (4 lines) ============
        // Line 0: {symbols joined by " · "}              ← Confluence symbols (closest to candle)
        // Line 1: {confidence} {confluence}              ← Base scores
        // Line 2: →{takeItScore}💰                       ← Recommendations (TAKE IT indicator)
        // Line 3: ▲+5 or ▼-3                             ← Profitability (farthest from candle)
        if (marker.flatMode) {
          ctx.font = `${scaledFontSize}px Arial`;
          const spacing = scaledFontSize * 0.5;

          // Draw transparent rectangle around ENTIRE marker stack
          // Note: y already includes displacementAmount (from line 249)
          // IMPORTANT: stackWidth/stackHeight are calculated with base fontSize,
          // but we render with scaledFontSize (includes pixelRatio for high-DPI/mobile)
          // Must scale these values to match the rendered content
          const rectPadding = this._boxPadding * (scaledFontSize / 11); // Configurable padding
          const scaledStackWidth = marker.stackWidth * pixelRatio;
          const scaledStackHeight = marker.stackHeight * pixelRatio;
          const rectWidth = scaledStackWidth + rectPadding * 2;
          const rectHeight = scaledStackHeight + rectPadding * 2; // More vertical padding
          const rectX = x - rectWidth / 2;
          let rectY: number;
          if (marker.isBullish) {
            // Bullish: marker extends downward from y
            rectY = y - scaledFontSize / 2 - rectPadding;
          } else {
            // Bearish: marker extends upward from y
            rectY = y - scaledStackHeight + scaledFontSize / 2 - rectPadding;
          }

          ctx.save();

          // Draw background fill first (no shadow)
          ctx.fillStyle = 'rgba(30, 41, 59, 0.4)';
          const radius = 4;
          ctx.beginPath();
          ctx.moveTo(rectX + radius, rectY);
          ctx.lineTo(rectX + rectWidth - radius, rectY);
          ctx.quadraticCurveTo(rectX + rectWidth, rectY, rectX + rectWidth, rectY + radius);
          ctx.lineTo(rectX + rectWidth, rectY + rectHeight - radius);
          ctx.quadraticCurveTo(rectX + rectWidth, rectY + rectHeight, rectX + rectWidth - radius, rectY + rectHeight);
          ctx.lineTo(rectX + radius, rectY + rectHeight);
          ctx.quadraticCurveTo(rectX, rectY + rectHeight, rectX, rectY + rectHeight - radius);
          ctx.lineTo(rectX, rectY + radius);
          ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
          ctx.closePath();
          ctx.fill();

          // Now draw border with glow (shadow only affects stroke)
          const nonSkippedConfidences = symbols
            .filter(sym => !symbolSkippedMap.get(sym))
            .map(sym => symbolConfidenceMap.get(sym) ?? 0);
          const maxConfidence = nonSkippedConfidences.length > 0
            ? Math.max(...nonSkippedConfidences)
            : 0;

          if (maxConfidence >= 75) {
            ctx.strokeStyle = '#fbbf24';
            ctx.shadowColor = '#fbbf24';
            ctx.shadowBlur = 8;
            ctx.lineWidth = 2;
          } else if (maxConfidence >= 65) {
            ctx.strokeStyle = '#22c55e';
            ctx.shadowColor = '#22c55e';
            ctx.shadowBlur = 4;
            ctx.lineWidth = 1.5;
          } else {
            ctx.strokeStyle = 'rgba(100, 116, 139, 0.5)';
            ctx.lineWidth = 1;
          }
          ctx.stroke();
          ctx.restore();

          // ---- LINE 0-1: Confluence symbols with points stacked (closest to candle) ----
          // Symbol on top, points underneath - color-coded by confidence
          if (symbols.length > 0) {
            const smallFontSize = scaledFontSize * 0.8;
            const pairSpacing = scaledFontSize * 1.2; // Space between symbol pairs
            const stackGap = scaledFontSize * 0.4; // Gap between symbol and score

            // Build data for each symbol
            const symbolData = symbols.map(sym => {
              const confidence = symbolConfidenceMap.get(sym) ?? 0;
              const isCross = isCrossIndex(primarySymbol, sym);

              // Use explicit skipped flag from backend
              const isSkipped = symbolSkippedMap.get(sym) ?? false;

              // Determine color, style, and icon based on confidence score
              let color: string;
              let bold = false;
              let glow = false;
              let icon: string;
              if (isSkipped) {
                color = '#ef4444'; // red-500 - skipped signal
                icon = '✗';
              } else if (confidence < 40) {
                color = '#6b7280'; // gray-500 - very low confidence
                icon = '·';
              } else if (confidence < 55) {
                color = '#9ca3af'; // gray-400 - low confidence
                icon = '○';
              } else if (confidence < 65) {
                color = '#fbbf24'; // amber-400 - medium confidence
                bold = true;
                icon = '◆';
              } else if (confidence < 75) {
                color = '#22c55e'; // green-500 - good confidence
                bold = true;
                icon = '✓';
              } else if (confidence < 85) {
                color = '#10b981'; // emerald-500 - high confidence
                bold = true;
                glow = true;
                icon = '★';
              } else {
                color = '#06b6d4'; // cyan-500 - excellent confidence
                bold = true;
                glow = true;
                icon = '⬆';
              }

              // Cross-index override (only if not skipped) - gold for cross-index
              if (isCross && !isSkipped) {
                color = confidence >= 70 ? '#fbbf24' : '#f59e0b'; // brighter gold for high conf cross
                bold = true;
                glow = confidence >= 75;
              }

              return { sym, confidence: confidence ?? 0, color, bold, isSkipped, glow, icon };
            });

            // Calculate widths for centering (icon + symbol on top, score below)
            ctx.font = `${smallFontSize}px Arial`;
            const pairWidths = symbolData.map(d => {
              const iconSymWidth = ctx.measureText(`${d.icon}${d.sym}`).width;
              // Always show confidence score, even for skipped symbols
              const confWidth = ctx.measureText(`${d.confidence}`).width;
              return Math.max(iconSymWidth, confWidth);
            });
            const totalWidth = pairWidths.reduce((a, b) => a + b, 0) + pairSpacing * (symbols.length - 1);

            // Draw each symbol/score pair
            let drawX = x - totalWidth / 2;
            const line0Y = y; // Symbol row
            const line1Y = marker.isBullish ? y + smallFontSize + stackGap : y - smallFontSize - stackGap; // Score row

            for (let i = 0; i < symbolData.length; i++) {
              const d = symbolData[i];
              const pairCenterX = drawX + pairWidths[i] / 2;

              // Apply glow effect for high confidence
              if (d.glow) {
                ctx.shadowColor = d.color;
                ctx.shadowBlur = 6 * (scaledFontSize / 11);
              }

              // Draw icon + symbol name
              const displayText = `${d.icon}${d.sym}`;
              ctx.font = d.bold ? `bold ${smallFontSize}px Arial` : `${smallFontSize}px Arial`;
              ctx.fillStyle = d.color;
              ctx.textAlign = 'center';
              ctx.fillText(displayText, pairCenterX, line0Y);

              // Draw strikethrough for skipped symbols (through symbol only, not icon)
              if (d.isSkipped) {
                ctx.shadowBlur = 0; // No glow for strikethrough
                const fullWidth = ctx.measureText(displayText).width;
                const iconWidth = ctx.measureText(d.icon).width;
                ctx.strokeStyle = d.color;
                ctx.lineWidth = 1 * (scaledFontSize / 11); // Scale with font
                ctx.beginPath();
                // Strike through only the symbol part (after icon)
                const symStartX = pairCenterX - fullWidth / 2 + iconWidth;
                const symEndX = pairCenterX + fullWidth / 2 + 2;
                ctx.moveTo(symStartX, line0Y);
                ctx.lineTo(symEndX, line0Y);
                ctx.stroke();
              }

              // Draw confidence score underneath (same size as symbol)
              // Always show confidence score, even for skipped symbols
              ctx.font = d.bold ? `bold ${smallFontSize}px Arial` : `${smallFontSize}px Arial`;
              ctx.fillText(`${d.confidence}`, pairCenterX, line1Y);

              // Reset shadow
              ctx.shadowBlur = 0;

              drawX += pairWidths[i] + pairSpacing;
            }

            ctx.textAlign = 'center';
            ctx.font = `${scaledFontSize}px Arial`;
          }

          // ---- LINE 2: Base scores (confidence, confluence) ----
          // Offset by 2 lines since confluence symbols take 2 rows
          const hasSymbols = symbols.length > 0;
          const symbolRowOffset = hasSymbols ? 2 : 0;
          let line2Parts: { text: string; color: string }[] = [];
          line2Parts.push({ text: confText, color: confidenceColor });
          if (hasConfluence) {
            line2Parts.push({ text: cfText, color: confluenceColor });
          }

          const line2Y = marker.isBullish ? y + lineHeight * symbolRowOffset : y - lineHeight * symbolRowOffset;
          {
            let totalWidth = 0;
            const partWidths: number[] = [];
            for (const part of line2Parts) {
              const w = ctx.measureText(part.text).width;
              partWidths.push(w);
              totalWidth += w;
            }
            totalWidth += spacing * (line2Parts.length - 1);

            let drawX = x - totalWidth / 2;
            for (let i = 0; i < line2Parts.length; i++) {
              ctx.fillStyle = line2Parts[i].color;
              ctx.textAlign = 'left';
              ctx.fillText(line2Parts[i].text, drawX, line2Y);
              drawX += partWidths[i] + spacing;
            }
          }

          // ---- LINE 2: Recommendations (take-it, ML) ----
          let line3Parts: { text: string; color: string }[] = [];
          if (showTakeIt) {
            line3Parts.push({ text: `→${Math.round(combinedScore)}💰`, color: '#ff00ff' });
          }
          if (hasML) {
            const separator = line3Parts.length > 0 ? '| ' : '';
            line3Parts.push({ text: `${separator}${mlText}`, color: mlColor });
          }

          const line3Y = marker.isBullish ? y + lineHeight * (symbolRowOffset + 1) : y - lineHeight * (symbolRowOffset + 1);
          if (line3Parts.length > 0) {
            let totalWidth = 0;
            const partWidths: number[] = [];
            for (const part of line3Parts) {
              const w = ctx.measureText(part.text).width;
              partWidths.push(w);
              totalWidth += w;
            }
            totalWidth += spacing * (line3Parts.length - 1);

            let drawX = x - totalWidth / 2;
            for (let i = 0; i < line3Parts.length; i++) {
              ctx.fillStyle = line3Parts[i].color;
              ctx.textAlign = 'left';
              ctx.fillText(line3Parts[i].text, drawX, line3Y);
              drawX += partWidths[i] + spacing;
            }
          }

          // ---- LINE 3: Profitability with MAE (farthest from candle) ----
          if (marker.isProfitable !== undefined && marker.pnl !== undefined) {
            const line4Y = marker.isBullish ? y + lineHeight * (symbolRowOffset + 2) : y - lineHeight * (symbolRowOffset + 2);
            const pnlColor = marker.isProfitable ? '#00FFFF' : '#FF00FF'; // Cyan for profit, Magenta for loss
            const arrow = marker.isProfitable ? '▲' : '▼';
            // Include MAE in the pnl text if available
            const maeStr = marker.maePercent !== undefined ? ` ${Math.round(marker.maePercent)}%` : '';
            const pnlText = `${arrow}${marker.pnl > 0 ? '+' : ''}${Math.round(marker.pnl)}${maeStr}`;

            // Color the whole text - use MAE color if MAE is bad (>50%)
            const useRedForBadMae = marker.maePercent !== undefined && marker.maePercent > 50;
            const textColor = useRedForBadMae ? '#f87171' : pnlColor;
            ctx.fillStyle = textColor;
            ctx.textAlign = 'center';
            ctx.fillText(pnlText, x, line4Y);
          }

          // Reset for next marker
          ctx.textAlign = 'center';
          ctx.font = `${scaledFontSize}px Arial`;
          continue; // Skip the stacked rendering below
        }

        // ============ STACKED MODE (original vertical layout) ============
        const takeItEmoji = `💰💲`;
        const takeItScore = `${Math.round(combinedScore)}`;
        const takeItFontSize = scaledFontSize * 1.2;
        const crossIndexFontSize = scaledFontSize * 1.2;

        if (hasConfluence) {
          const mlOffset = hasML ? 1 : 0;

          if (marker.isBullish) {
            // Bullish: below candle - confidence, ML, confluence, symbols, TAKE IT (top to bottom)
            ctx.fillStyle = confidenceColor;
            ctx.fillText(confText, x, y);
            if (hasML) {
              ctx.fillStyle = mlColor;
              ctx.fillText(mlText, x, y + lineHeight);
            }
            ctx.fillStyle = confluenceColor;
            ctx.fillText(cfText, x, y + lineHeight * (1 + mlOffset));

            // Draw transparent rectangle around symbols section
            if (symbols.length > 0) {
              const smallFont = scaledFontSize * 0.85;
              ctx.font = `bold ${smallFont}px Arial`;
              // Calculate max width of all symbol texts
              let maxSymWidth = 0;
              symbols.forEach((sym) => {
                const confidence = symbolConfidenceMap.get(sym) ?? 0;
                const symText = confidence !== undefined ? `★${sym}:${confidence}` : `★${sym}:–`;
                const width = ctx.measureText(symText).width;
                if (width > maxSymWidth) maxSymWidth = width;
              });
              const padding = 4 * (scaledFontSize / 11);
              const rectWidth = maxSymWidth + padding * 2;
              const rectHeight = symbols.length * lineHeight + padding;
              const rectX = x - rectWidth / 2;
              const rectY = y + lineHeight * (1.5 + mlOffset);

              // Draw rounded rectangle background
              const radius = 3;
              ctx.save();
              ctx.fillStyle = 'rgba(30, 41, 59, 0.75)';
              ctx.strokeStyle = 'rgba(100, 116, 139, 0.8)';
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(rectX + radius, rectY);
              ctx.lineTo(rectX + rectWidth - radius, rectY);
              ctx.quadraticCurveTo(rectX + rectWidth, rectY, rectX + rectWidth, rectY + radius);
              ctx.lineTo(rectX + rectWidth, rectY + rectHeight - radius);
              ctx.quadraticCurveTo(rectX + rectWidth, rectY + rectHeight, rectX + rectWidth - radius, rectY + rectHeight);
              ctx.lineTo(rectX + radius, rectY + rectHeight);
              ctx.quadraticCurveTo(rectX, rectY + rectHeight, rectX, rectY + rectHeight - radius);
              ctx.lineTo(rectX, rectY + radius);
              ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
              ctx.closePath();
              ctx.fill();
              ctx.stroke();
              ctx.restore();
              ctx.font = `${scaledFontSize}px Arial`;
            }

            symbols.forEach((sym, idx) => {
              const isCross = isCrossIndex(primarySymbol, sym);
              const confidence = symbolConfidenceMap.get(sym) ?? 0;
              // Use explicit skipped flag from backend
              const isSkipped = symbolSkippedMap.get(sym) ?? false;
              // Determine color, style, and icon based on confidence
              let symColor: string;
              let symBold = false;
              let symGlow = false;
              let icon: string;
              if (isSkipped) {
                symColor = '#ef4444'; icon = '✗';
              } else if (confidence < 40) {
                symColor = '#6b7280'; icon = '·';
              } else if (confidence < 55) {
                symColor = '#9ca3af'; icon = '○';
              } else if (confidence < 65) {
                symColor = '#fbbf24'; symBold = true; icon = '◆';
              } else if (confidence < 75) {
                symColor = '#22c55e'; symBold = true; icon = '✓';
              } else if (confidence < 85) {
                symColor = '#10b981'; symBold = true; symGlow = true; icon = '★';
              } else {
                symColor = '#06b6d4'; symBold = true; symGlow = true; icon = '⬆';
              }
              if (isCross && !isSkipped) {
                symColor = confidence >= 70 ? '#fbbf24' : '#f59e0b';
                symBold = true;
                symGlow = confidence >= 75;
              }
              // Format: icon + SYM:confidence
              const symText = `${icon}${sym}:${confidence}`;
              const smallFont = scaledFontSize * 0.85;
              if (symGlow) {
                ctx.shadowColor = symColor;
                ctx.shadowBlur = 6 * (scaledFontSize / 11);
              }
              ctx.font = symBold ? `bold ${smallFont}px Arial` : `${smallFont}px Arial`;
              ctx.fillStyle = symColor;
              const symY = y + lineHeight * (2 + mlOffset + idx);
              ctx.fillText(symText, x, symY);
              ctx.shadowBlur = 0;
              // Draw strikethrough for skipped (through symbol:conf, not icon)
              if (isSkipped) {
                const fullWidth = ctx.measureText(symText).width;
                const iconWidth = ctx.measureText(icon).width;
                ctx.strokeStyle = symColor;
                ctx.lineWidth = 1 * (scaledFontSize / 11);
                ctx.beginPath();
                ctx.moveTo(x - fullWidth / 2 + iconWidth, symY);
                ctx.lineTo(x + fullWidth / 2 + 2, symY);
                ctx.stroke();
              }
            });
            ctx.font = `${scaledFontSize}px Arial`;
            if (showTakeIt) {
              ctx.font = `bold ${takeItFontSize}px Arial`;
              ctx.fillStyle = '#ff00ff';
              const takeItBaseY = y + lineHeight * (2 + mlOffset + symbols.length + 0.5);
              ctx.fillText(takeItEmoji, x, takeItBaseY);
              ctx.fillText(takeItScore, x, takeItBaseY + lineHeight * 1.4);
              ctx.font = `${scaledFontSize}px Arial`;
            }
          } else {
            // Bearish: above candle - TAKE IT, symbols, confluence, ML, confidence
            if (showTakeIt) {
              ctx.font = `bold ${takeItFontSize}px Arial`;
              ctx.fillStyle = '#ff00ff';
              const takeItBaseY = y - lineHeight * (2 + mlOffset + symbols.length + 0.5);
              ctx.fillText(takeItEmoji, x, takeItBaseY);
              ctx.fillText(takeItScore, x, takeItBaseY - lineHeight * 1.4);
              ctx.font = `${scaledFontSize}px Arial`;
            }

            // Draw transparent rectangle around symbols section
            if (symbols.length > 0) {
              const smallFont = scaledFontSize * 0.85;
              ctx.font = `bold ${smallFont}px Arial`;
              // Calculate max width of all symbol texts
              let maxSymWidth = 0;
              symbols.forEach((sym) => {
                const confidence = symbolConfidenceMap.get(sym) ?? 0;
                const symText = confidence !== undefined ? `★${sym}:${confidence}` : `★${sym}:–`;
                const width = ctx.measureText(symText).width;
                if (width > maxSymWidth) maxSymWidth = width;
              });
              const padding = 4 * (scaledFontSize / 11);
              const rectWidth = maxSymWidth + padding * 2;
              const rectHeight = symbols.length * lineHeight + padding;
              const rectX = x - rectWidth / 2;
              const rectY = y - lineHeight * (1.5 + mlOffset + symbols.length) - padding / 2;

              // Draw rounded rectangle background
              const radius = 3;
              ctx.save();
              ctx.fillStyle = 'rgba(30, 41, 59, 0.75)';
              ctx.strokeStyle = 'rgba(100, 116, 139, 0.8)';
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(rectX + radius, rectY);
              ctx.lineTo(rectX + rectWidth - radius, rectY);
              ctx.quadraticCurveTo(rectX + rectWidth, rectY, rectX + rectWidth, rectY + radius);
              ctx.lineTo(rectX + rectWidth, rectY + rectHeight - radius);
              ctx.quadraticCurveTo(rectX + rectWidth, rectY + rectHeight, rectX + rectWidth - radius, rectY + rectHeight);
              ctx.lineTo(rectX + radius, rectY + rectHeight);
              ctx.quadraticCurveTo(rectX, rectY + rectHeight, rectX, rectY + rectHeight - radius);
              ctx.lineTo(rectX, rectY + radius);
              ctx.quadraticCurveTo(rectX, rectY, rectX + radius, rectY);
              ctx.closePath();
              ctx.fill();
              ctx.stroke();
              ctx.restore();
              ctx.font = `${scaledFontSize}px Arial`;
            }

            symbols.forEach((sym, idx) => {
              const isCross = isCrossIndex(primarySymbol, sym);
              const confidence = symbolConfidenceMap.get(sym) ?? 0;
              // Use explicit skipped flag from backend
              const isSkipped = symbolSkippedMap.get(sym) ?? false;
              // Determine color, style, and icon based on confidence
              let symColor: string;
              let symBold = false;
              let symGlow = false;
              let icon: string;
              if (isSkipped) {
                symColor = '#ef4444'; icon = '✗';
              } else if (confidence < 40) {
                symColor = '#6b7280'; icon = '·';
              } else if (confidence < 55) {
                symColor = '#9ca3af'; icon = '○';
              } else if (confidence < 65) {
                symColor = '#fbbf24'; symBold = true; icon = '◆';
              } else if (confidence < 75) {
                symColor = '#22c55e'; symBold = true; icon = '✓';
              } else if (confidence < 85) {
                symColor = '#10b981'; symBold = true; symGlow = true; icon = '★';
              } else {
                symColor = '#06b6d4'; symBold = true; symGlow = true; icon = '⬆';
              }
              if (isCross && !isSkipped) {
                symColor = confidence >= 70 ? '#fbbf24' : '#f59e0b';
                symBold = true;
                symGlow = confidence >= 75;
              }
              // Format: icon + SYM:confidence
              const symText = `${icon}${sym}:${confidence}`;
              const smallFont = scaledFontSize * 0.85;
              if (symGlow) {
                ctx.shadowColor = symColor;
                ctx.shadowBlur = 6 * (scaledFontSize / 11);
              }
              ctx.font = symBold ? `bold ${smallFont}px Arial` : `${smallFont}px Arial`;
              ctx.fillStyle = symColor;
              const symY = y - lineHeight * (2 + mlOffset + symbols.length - 1 - idx);
              ctx.fillText(symText, x, symY);
              ctx.shadowBlur = 0;
              // Draw strikethrough for skipped (through symbol:conf, not icon)
              if (isSkipped) {
                const fullWidth = ctx.measureText(symText).width;
                const iconWidth = ctx.measureText(icon).width;
                ctx.strokeStyle = symColor;
                ctx.lineWidth = 1 * (scaledFontSize / 11);
                ctx.beginPath();
                ctx.moveTo(x - fullWidth / 2 + iconWidth, symY);
                ctx.lineTo(x + fullWidth / 2 + 2, symY);
                ctx.stroke();
              }
            });
            ctx.font = `${scaledFontSize}px Arial`;
            ctx.fillStyle = confluenceColor;
            ctx.fillText(cfText, x, y - lineHeight * (1 + mlOffset));
            if (hasML) {
              ctx.fillStyle = mlColor;
              ctx.fillText(mlText, x, y - lineHeight);
            }
            ctx.fillStyle = confidenceColor;
            ctx.fillText(confText, x, y);
          }
        } else {
          // Just confidence + ML (no confluence)
          const mlOffset = hasML ? 1 : 0;

          if (marker.isBullish) {
            ctx.fillStyle = confidenceColor;
            ctx.fillText(confText, x, y);
            if (hasML) {
              ctx.fillStyle = mlColor;
              ctx.fillText(mlText, x, y + lineHeight);
            }
            if (showTakeIt) {
              ctx.font = `bold ${takeItFontSize}px Arial`;
              ctx.fillStyle = '#ff00ff';
              const takeItBaseY = y + lineHeight * (1.5 + mlOffset);
              ctx.fillText(takeItEmoji, x, takeItBaseY);
              ctx.fillText(takeItScore, x, takeItBaseY + lineHeight * 1.4);
              ctx.font = `${scaledFontSize}px Arial`;
            }
          } else {
            if (showTakeIt) {
              ctx.font = `bold ${takeItFontSize}px Arial`;
              ctx.fillStyle = '#ff00ff';
              const takeItBaseY = y - lineHeight * (1.5 + mlOffset);
              ctx.fillText(takeItEmoji, x, takeItBaseY);
              ctx.fillText(takeItScore, x, takeItBaseY - lineHeight * 1.4);
              ctx.font = `${scaledFontSize}px Arial`;
            }
            if (hasML) {
              ctx.fillStyle = mlColor;
              ctx.fillText(mlText, x, y - lineHeight);
            }
            ctx.fillStyle = confidenceColor;
            ctx.fillText(confText, x, y);
          }
        }

        // ---- PROFITABILITY with MAE (farthest from candle, after all other elements) ----
        if (marker.isProfitable !== undefined && marker.pnl !== undefined) {
          const pnlColor = marker.isProfitable ? '#00FFFF' : '#FF00FF';
          const arrow = marker.isProfitable ? '▲' : '▼';
          const pnlText = `${arrow}${marker.pnl > 0 ? '+' : ''}${Math.round(marker.pnl)}`;
          ctx.fillStyle = pnlColor;
          ctx.font = `${scaledFontSize}px Arial`;

          // Calculate position after entire stack
          const baseLines = hasConfluence ? (2 + (hasML ? 1 : 0) + symbols.length) : (1 + (hasML ? 1 : 0));
          const takeItLines = showTakeIt ? 3 : 0;
          const totalLines = baseLines + takeItLines + 1;

          const profitY = marker.isBullish ? y + lineHeight * totalLines : y - lineHeight * totalLines;
          ctx.fillText(pnlText, x, profitY);

          // Draw MAE if available (on same line, after P&L)
          if (marker.maePercent !== undefined) {
            const pnlWidth = ctx.measureText(pnlText).width;
            const maeText = `${Math.round(marker.maePercent)}%`;
            // Color MAE based on value: <30% green, 30-60% yellow, >60% red
            const maeColor = marker.maePercent < 30 ? '#86efac' : marker.maePercent < 60 ? '#fbbf24' : '#f87171';
            ctx.fillStyle = maeColor;
            ctx.textAlign = 'left';
            ctx.fillText(maeText, x + pnlWidth / 2 + scaledFontSize * 0.3, profitY);
            ctx.textAlign = 'center';
          }
        }
      }
    });
  }
}

class SignalMarkerPaneView implements IPrimitivePaneView {
  private _source: SignalMarkerPrimitive;
  private _renderer: SignalMarkerPaneRenderer;

  constructor(source: SignalMarkerPrimitive) {
    this._source = source;
    this._renderer = new SignalMarkerPaneRenderer();
  }

  zOrder(): PrimitivePaneViewZOrder {
    return 'top';
  }

  renderer(): IPrimitivePaneRenderer {
    const data = this._source.getRendererData();
    const expandedMarkers = this._source.getExpandedMarkers();
    const recentMarkers = this._source.getRecentMarkerTimestamps();
    this._renderer.update(
      data,
      this._source.getFontSize(),
      this._source.getLineHeightMultiplier(),
      this._source.getBoxPadding(),
      this._source.isShowTextOverlay(),
      expandedMarkers,
      recentMarkers,
      (positions) => this._source.setCirclePositions(positions)
    );
    return this._renderer;
  }
}

export class SignalMarkerPrimitive implements ISeriesPrimitive<Time> {
  private _markers: SignalMarkerData[] = [];
  private _paneView: SignalMarkerPaneView;
  private _requestUpdate?: () => void;
  private _series?: SeriesAttachedParameter<Time>['series'];
  private _chart?: SeriesAttachedParameter<Time>['chart'];
  private _fontSize: number = 11; // Small font size for confidence labels
  private _offset: number = 35; // Offset from candle high/low in pixels (below the main marker dot)
  private _showTextOverlay: boolean = true; // Show/hide the text overlay (confidence, confluence, etc.)

  // Collision detection - hardcoded optimal values
  private _collisionEnabled: boolean = true; // Disable on mobile
  private _lineHeightMultiplier: number = 1.5; // Line spacing between text elements
  private _boxPadding: number = 6; // Padding around marker boxes

  // Canvas for text measurement (created lazily)
  private _measureCanvas: HTMLCanvasElement | null = null;
  private _measureCtx: CanvasRenderingContext2D | null = null;

  // Candle bounds for marker-candle collision detection
  private _candleBounds: CandleBounds[] = [];
  private _candleBoundsMap: Map<number, CandleBounds> = new Map(); // time -> bounds for O(1) lookup

  // Click-to-expand tracking: marker timestamp -> expiry timestamp
  private _expandedMarkers: Map<number, number> = new Map();
  private _expandDuration: number = 60000; // 1 minute
  private _recentMarkersCount: number = 0; // No auto-expand; markers render as dots until clicked or global toggle

  // Circle positions for click detection (updated each render)
  private _circlePositions: { time: number; x: number; y: number; radius: number }[] = [];

  private _layout = new MarkerLayoutClient('signal', (ctx) => this._layoutItems(ctx));

  constructor() {
    this._paneView = new SignalMarkerPaneView(this);
  }

  // Set candle bounds for collision detection with candle bodies/wicks
  setCandleBounds(bounds: CandleBounds[]): void {
    this._candleBounds = bounds;
    this._candleBoundsMap.clear();
    for (const b of bounds) {
      this._candleBoundsMap.set(b.time, b);
    }
  }

  // Lazy initialization of measurement canvas
  private _getMeasureContext(): CanvasRenderingContext2D {
    if (!this._measureCtx) {
      this._measureCanvas = document.createElement('canvas');
      this._measureCtx = this._measureCanvas.getContext('2d')!;
    }
    return this._measureCtx;
  }

  attached(param: SeriesAttachedParameter<Time>): void {
    this._series = param.series;
    this._chart = param.chart;
    this._requestUpdate = param.requestUpdate;
    this._layout.setAttached(true);
  }

  detached(): void {
    this._layout.setAttached(false);
    this._series = undefined;
    this._chart = undefined;
    this._requestUpdate = undefined;
  }

  /** Join the chart's shared marker layout (null: its own collision pass). */
  setMarkerLayout(reg: MarkerLayoutRegistry | null): void {
    this._layout.setRegistry(reg);
  }

  /**
   * Two boxes per marker for the shared layout: the ring dot (close to the
   * candle) and, when its text shows, the score box (further out, low
   * priority, joined back to the dot by the leader line).
   */
  private _layoutItems(ctx: LayoutContext): LayoutItem[] {
    const out: LayoutItem[] = [];
    const flatMode = this._collisionEnabled;
    const expanded = this.getExpandedMarkers();
    const recent = this.getRecentMarkerTimestamps();
    const pad = this._boxPadding * (this._fontSize / 11);
    for (const marker of this._markers) {
      const t = Math.floor(marker.time / 1000);
      if (t < ctx.fromTime || t > ctx.toTime) continue;
      const x = ctx.timeToX(t);
      const priceY = ctx.priceToY(marker.price);
      if (x === null || priceY === null) continue;
      const side = marker.isBullish ? 'below' : 'above';
      out.push({
        key: `dot:${marker.time}:${side}`,
        time: t,
        x,
        side,
        anchorY: priceY,
        w: DOT_BOX,
        h: DOT_BOX,
        gap: DOT_OFFSET - DOT_BOX / 2,
        priority: LAYOUT_PRIORITY.signalDot,
        color: marker.isBullish ? '#26a69a' : '#ef5350',
        label: `signal ${Math.round(marker.confidence)}`,
        ref: marker,
      });
      if (this._showTextOverlay || expanded.has(marker.time) || recent.has(marker.time)) {
        out.push({
          key: `text:${marker.time}:${side}`,
          time: t,
          x,
          side,
          anchorY: priceY,
          w: Math.ceil(this._estimateStackWidth(marker, flatMode) + pad * 2),
          h: Math.ceil(this._estimateStackHeight(marker, flatMode) + pad * 2),
          gap: 4,
          priority: LAYOUT_PRIORITY.signalText,
          // Its own dashed leader runs back to the dot.
          noLeader: true,
          label: `signal ${Math.round(marker.confidence)} scores`,
          ref: marker,
        });
      }
    }
    return out;
  }

  /** Renderer data straight from the shared layout's boxes. */
  private _sharedRendererData(placed: readonly PlacedItem[]): SignalMarkerRendererData[] {
    const flatMode = this._collisionEnabled;
    const pad = this._boxPadding * (this._fontSize / 11);
    const dots = new Map<SignalMarkerData, { x: number; cy: number }>();
    const texts = new Map<SignalMarkerData, { x: number; top: number; h: number }>();
    for (const p of placed) {
      const m = p.item.ref as SignalMarkerData;
      if (p.item.key.startsWith('dot:')) dots.set(m, { x: p.item.x, cy: p.top + p.h / 2 });
      else texts.set(m, { x: p.left + p.w / 2, top: p.top, h: p.h });
    }
    const data: SignalMarkerRendererData[] = [];
    for (const [marker, dot] of dots) {
      const text = texts.get(marker);
      // The renderer draws the dot DOT_OFFSET off priceY: hand it the priceY that lands it on its box.
      const priceY = dot.cy + (marker.isBullish ? -DOT_OFFSET : DOT_OFFSET);
      const y = text
        ? marker.isBullish
          ? text.top + pad + this._fontSize / 2
          : text.top + text.h - pad - this._fontSize / 2
        : dot.cy;
      data.push({
        x: text ? text.x : dot.x,
        y,
        isBullish: marker.isBullish,
        confidence: marker.confidence,
        confluence: marker.confluence,
        confluenceSymbols: marker.confluenceSymbols,
        confluenceDetails: marker.confluenceDetails,
        primarySymbol: marker.primarySymbol,
        takeItThreshold: marker.takeItThreshold,
        takeItEnabled: marker.takeItEnabled,
        mlScore: marker.mlScore,
        mlRecommendation: marker.mlRecommendation,
        isProfitable: marker.isProfitable,
        pnl: marker.pnl,
        maePercent: marker.maePercent,
        isDisplaced: false,
        displacementAmount: 0,
        anchorY: y,
        anchorX: dot.x,
        priceY,
        markerTime: marker.time,
        stackHeight: this._estimateStackHeight(marker, flatMode),
        stackWidth: this._estimateStackWidth(marker, flatMode),
        flatMode,
        priority: this._calculatePriority(marker),
        horizontalOffset: 0,
        layer: 0,
        textHidden: !text,
      });
    }
    return data;
  }

  paneViews(): IPrimitivePaneView[] {
    return [this._paneView];
  }

  updateAllViews(): void {
    // Called when chart needs to update views
  }

  setMarkers(markers: SignalMarkerData[]): void {
    this._markers = markers;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setFontSize(size: number): void {
    this._fontSize = size;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  setOffset(offset: number): void {
    this._offset = offset;
    this._requestUpdate?.();
  }

  getFontSize(): number {
    return this._fontSize;
  }

  setCollisionEnabled(enabled: boolean): void {
    this._collisionEnabled = enabled;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  isCollisionEnabled(): boolean {
    return this._collisionEnabled;
  }

  getLineHeightMultiplier(): number {
    return this._lineHeightMultiplier;
  }

  getBoxPadding(): number {
    return this._boxPadding;
  }

  setShowTextOverlay(show: boolean): void {
    this._showTextOverlay = show;
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  isShowTextOverlay(): boolean {
    return this._showTextOverlay;
  }

  // Handle click on chart - returns true if a marker circle was clicked
  handleClick(clientX: number, clientY: number, chartRect: DOMRect): boolean {
    const x = clientX - chartRect.left;
    const y = clientY - chartRect.top;
    const isMobile = window.innerWidth <= 768;
    const clickRadius = isMobile ? 25 : 15; // Larger touch target on mobile

    for (const circle of this._circlePositions) {
      const dx = x - circle.x;
      const dy = y - circle.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      if (distance <= clickRadius) {
        this._toggleMarkerExpansion(circle.time);
        return true;
      }
    }
    return false;
  }

  // Toggle marker expansion (click to expand, click again to collapse)
  private _toggleMarkerExpansion(timestamp: number): void {
    const now = Date.now();

    if (this._expandedMarkers.has(timestamp)) {
      // Already expanded - collapse it
      this._expandedMarkers.delete(timestamp);
    } else {
      // Expand with 1 minute timeout
      this._expandedMarkers.set(timestamp, now + this._expandDuration);
    }
    this._layout.invalidate();
    this._requestUpdate?.();
  }

  // Clean up expired expansions
  private _cleanupExpiredExpansions(): void {
    const now = Date.now();
    for (const [timestamp, expiry] of this._expandedMarkers) {
      if (now > expiry) {
        this._expandedMarkers.delete(timestamp);
      }
    }
  }

  // Check if a marker should show its text overlay
  shouldShowTextForMarker(timestamp: number): boolean {
    // Always show if global overlay is on
    if (this._showTextOverlay) return true;

    // Check if manually expanded (and not expired)
    const expiry = this._expandedMarkers.get(timestamp);
    if (expiry && Date.now() <= expiry) return true;

    // Check if it's one of the recent markers
    const sortedMarkers = [...this._markers].sort((a, b) => b.time - a.time);
    const recentTimestamps = sortedMarkers.slice(0, this._recentMarkersCount).map(m => m.time);
    if (recentTimestamps.includes(timestamp)) return true;

    return false;
  }

  // Get expanded marker timestamps (for renderer)
  getExpandedMarkers(): Set<number> {
    this._cleanupExpiredExpansions();
    return new Set(this._expandedMarkers.keys());
  }

  // Get recent marker timestamps (for renderer)
  getRecentMarkerTimestamps(): Set<number> {
    const sortedMarkers = [...this._markers].sort((a, b) => b.time - a.time);
    return new Set(sortedMarkers.slice(0, this._recentMarkersCount).map(m => m.time));
  }

  // Update circle positions (called from renderer for click detection)
  setCirclePositions(positions: { time: number; x: number; y: number; radius: number }[]): void {
    this._circlePositions = positions;
  }

  // Calculate marker priority (higher = more important, stays in place)
  private _calculatePriority(marker: SignalMarkerData): number {
    let priority = marker.confidence;

    if (marker.mlRecommendation === 'TAKE') {
      priority += 20;
    }

    if (marker.confluence !== undefined && marker.confluence > 0) {
      priority += marker.confluence * 0.5;
    }

    return priority;
  }

  // Estimate text width for a marker (for accurate collision detection)
  private _estimateStackWidth(marker: SignalMarkerData, flatMode: boolean): number {
    const ctx = this._getMeasureContext();
    const scaledFontSize = this._fontSize;
    ctx.font = `${scaledFontSize}px Arial`;

    let maxWidth = 0;

    // Confidence text
    const confText = `${Math.round(marker.confidence)}`;
    maxWidth = Math.max(maxWidth, ctx.measureText(confText).width);

    // Confluence text
    if (marker.confluence !== undefined && marker.confluence !== 0) {
      const cfText = `${marker.confluence >= 0 ? '+' : ''}${Math.round(marker.confluence)}`;
      maxWidth = Math.max(maxWidth, ctx.measureText(cfText).width);
    }

    // ML scoring deprecated - no width calculation needed

    // Derive symbols from confluenceDetails if confluenceSymbols is empty
    let symbols = marker.confluenceSymbols || [];
    if (symbols.length === 0 && marker.confluenceDetails) {
      symbols = marker.confluenceDetails.map(d =>
        d.symbol.replace(/[A-Z]\d+\..*$/, '').replace(/\..*$/, '')
      );
    }

    // Confluence symbols (in flat mode, they're arranged horizontally with boxes)
    if (flatMode && symbols.length > 0) {
      const smallFontSize = scaledFontSize * 0.8;
      const pairSpacing = scaledFontSize * 1.2;
      ctx.font = `${smallFontSize}px Arial`;

      // Calculate total width like the rendering code does
      let totalWidth = 0;
      for (const sym of symbols) {
        const symText = `★${sym}`;
        const confWidth = ctx.measureText('99').width; // Estimate confidence width
        const symWidth = ctx.measureText(symText).width;
        totalWidth += Math.max(symWidth, confWidth);
      }
      totalWidth += pairSpacing * (symbols.length - 1);

      // Add box padding
      const rectPadding = 4 * (scaledFontSize / 11);
      totalWidth += rectPadding * 2;

      maxWidth = Math.max(maxWidth, totalWidth);
      ctx.font = `${scaledFontSize}px Arial`;
    }

    // Add general padding
    return maxWidth + 10;
  }

  // Calculate the estimated stack height for a marker (number of lines * line height)
  private _estimateStackHeight(marker: SignalMarkerData, flatMode: boolean): number {
    const lineHeight = this._fontSize * this._lineHeightMultiplier;

    // Flat mode: count actual rendered lines
    // IMPORTANT: Profitability is always at fixed offset (symbolRowOffset + 2)
    // even if recommendations line is empty, so we must account for the gap
    if (flatMode) {
      // Derive symbols from confluenceDetails if confluenceSymbols is empty
      let symbols = marker.confluenceSymbols || [];
      if (symbols.length === 0 && marker.confluenceDetails) {
        symbols = marker.confluenceDetails.map(d =>
          d.symbol.replace(/[A-Z]\d+\..*$/, '').replace(/\..*$/, '')
        );
      }
      const hasSymbols = symbols.length > 0;
      const symbolRowOffset = hasSymbols ? 2 : 0;

      const hasProfitability = marker.isProfitable !== undefined && marker.pnl !== undefined;

      if (hasProfitability) {
        // Profitability is at symbolRowOffset + 2, so total lines = symbolRowOffset + 3
        return (symbolRowOffset + 3) * lineHeight;
      }

      // No profitability - count actual lines
      let lines = 0;
      if (hasSymbols) lines += 2;
      lines += 1; // Base scores always present
      // Recommendations line
      const takeItEnabled = marker.takeItEnabled !== false;
      const takeItThreshold = marker.takeItThreshold ?? 80;
      const combinedScore = marker.confidence + (marker.confluence ?? 0);
      const showTakeIt = takeItEnabled && combinedScore > takeItThreshold;
      // ML scoring deprecated
      const hasML = false;
      if (showTakeIt) lines += 1;

      return lines * lineHeight;
    }

    // Stacked mode: calculate full height
    let lines = 1; // Confidence score always present

    // ML scoring deprecated - no line calculation needed

    // Confluence
    if (marker.confluence !== undefined && marker.confluence !== 0) {
      lines += 1; // Confluence score
      lines += (marker.confluenceSymbols?.length || 0); // Each symbol
    }

    // TAKE IT indicator
    const takeItEnabled = marker.takeItEnabled !== false;
    const takeItThreshold = marker.takeItThreshold ?? 80;
    const combinedScore = marker.confidence + (marker.confluence ?? 0);
    if (takeItEnabled && combinedScore > takeItThreshold) {
      lines += 2; // Emoji + score
    }

    // Profitability
    if (marker.isProfitable !== undefined && marker.pnl !== undefined) {
      lines += 1;
    }

    return lines * lineHeight;
  }

  getRendererData(): SignalMarkerRendererData[] {
    if (!this._series || !this._chart) return [];
    const shared = this._layout.placed();
    if (shared) return this._sharedRendererData(shared);

    const timeScale = this._chart.timeScale();
    const data: SignalMarkerRendererData[] = [];

    // Use flat mode when collision detection is enabled
    const flatMode = this._collisionEnabled;

    // First pass: calculate all positions, stack heights, widths, and priorities
    for (const marker of this._markers) {
      // Convert timestamp to Time format (seconds)
      const timeInSeconds = Math.floor(marker.time / 1000) as Time;

      // Get x coordinate from time
      const x = timeScale.timeToCoordinate(timeInSeconds);
      if (x === null) continue;

      // Get y coordinate from price with offset
      const priceY = this._series.priceToCoordinate(marker.price);
      if (priceY === null) continue;

      // Apply pixel offset - position text further from candle than the main marker
      const y = marker.isBullish
        ? priceY + this._offset  // Below candle
        : priceY - this._offset; // Above candle

      // When text overlay is off (minimized), use small circle dimensions for collision
      // instead of full text box dimensions — prevents false displacement of dots
      const stackHeight = this._showTextOverlay
        ? this._estimateStackHeight(marker, flatMode)
        : 14; // Circle diameter (~7px radius * 2)
      const stackWidth = this._showTextOverlay
        ? this._estimateStackWidth(marker, flatMode)
        : 14; // Circle diameter
      const priority = this._calculatePriority(marker);

      data.push({
        x,
        y,
        isBullish: marker.isBullish,
        confidence: marker.confidence,
        confluence: marker.confluence,
        confluenceSymbols: marker.confluenceSymbols,
        confluenceDetails: marker.confluenceDetails,
        primarySymbol: marker.primarySymbol,
        takeItThreshold: marker.takeItThreshold,
        takeItEnabled: marker.takeItEnabled,
        mlScore: marker.mlScore,
        mlRecommendation: marker.mlRecommendation,
        isProfitable: marker.isProfitable,
        pnl: marker.pnl,
        maePercent: marker.maePercent,
        isDisplaced: false,
        displacementAmount: 0,
        anchorY: y,
        anchorX: x,
        priceY,
        markerTime: marker.time,
        stackHeight,
        stackWidth,
        flatMode,
        priority,
        horizontalOffset: 0,
        layer: 0,
      });
    }

    // Second pass: detect collisions and displace overlapping markers (desktop only)
    if (!this._collisionEnabled) {
      return data;
    }

    return this._resolveCollisions(data);
  }

  // ============================================================================
  // COLLISION RESOLUTION - Simple layered algorithm with hardcoded optimal values
  // See collisionDetection.ts for extracted testable functions
  // ============================================================================

  // Import collision constants for consistency with unit tests
  // These values are also defined in collisionDetection.ts
  private static readonly MIN_X_GAP = 5;   // Buffer for X overlap detection
  private static readonly MIN_Y_GAP = 8;   // Minimum vertical gap between markers/candles

  // Main collision resolution - assigns markers to layers to avoid overlap
  private _resolveCollisions(data: SignalMarkerRendererData[]): SignalMarkerRendererData[] {
    // Separate bullish and bearish markers
    const bullish = data.filter(m => m.isBullish);
    const bearish = data.filter(m => !m.isBullish);

    // Assign layers to prevent X overlap
    this._assignLayers(bullish, true);
    this._assignLayers(bearish, false);

    // Resolve any remaining marker-marker collisions
    this._resolveRemainingCollisions(data);

    // Resolve marker-candle collisions
    this._resolveMarkerCandleCollisions(data);

    // Final pass to fix any collisions caused by candle avoidance
    this._resolveRemainingCollisions(data);

    return data;
  }

  // Assign markers to non-overlapping layers
  private _assignLayers(markers: SignalMarkerRendererData[], isBullish: boolean): void {
    if (markers.length === 0) return;

    // Sort by x coordinate, then by priority (higher priority = placed first)
    markers.sort((a, b) => {
      if (a.x !== b.x) return a.x - b.x;
      return b.priority - a.priority;
    });

    const boxPadding = this._boxPadding;

    // Layers: each layer is a list of markers with their x-ranges
    interface LayerEntry {
      marker: SignalMarkerRendererData;
      xMin: number;
      xMax: number;
    }
    const layers: LayerEntry[][] = [[]];

    for (const marker of markers) {
      const halfWidth = marker.stackWidth / 2 + boxPadding;
      const xMin = marker.x - halfWidth;
      const xMax = marker.x + halfWidth;

      // Find first layer where this marker fits (no X overlap)
      let assigned = false;
      for (let layerIdx = 0; layerIdx < layers.length; layerIdx++) {
        const layer = layers[layerIdx];
        let fits = true;

        for (const entry of layer) {
          // Check X overlap (with small buffer)
          if (xMin < entry.xMax + SignalMarkerPrimitive.MIN_X_GAP &&
              xMax > entry.xMin - SignalMarkerPrimitive.MIN_X_GAP) {
            fits = false;
            break;
          }
        }

        if (fits) {
          marker.layer = layerIdx;
          layer.push({ marker, xMin, xMax });
          assigned = true;
          break;
        }
      }

      // Create new layer if needed
      if (!assigned) {
        marker.layer = layers.length;
        layers.push([{ marker, xMin, xMax }]);
      }
    }

    // Calculate vertical displacement based on layer
    for (const marker of markers) {
      if (marker.layer > 0) {
        let cumulativeHeight = 0;
        for (let l = 0; l < marker.layer; l++) {
          const layerMarkers = markers.filter(m => m.layer === l);
          const maxHeight = layerMarkers.length > 0
            ? Math.max(...layerMarkers.map(m => m.stackHeight + boxPadding * 2))
            : marker.stackHeight + boxPadding * 2;
          cumulativeHeight += maxHeight + SignalMarkerPrimitive.MIN_Y_GAP;
        }

        marker.displacementAmount = isBullish ? cumulativeHeight : -cumulativeHeight;
        marker.isDisplaced = true;
      }
    }
  }

  // Iteratively resolve any remaining collisions
  private _resolveRemainingCollisions(data: SignalMarkerRendererData[]): void {
    const maxIterations = 10;

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      let anyDisplaced = false;

      for (let i = 0; i < data.length; i++) {
        for (let j = i + 1; j < data.length; j++) {
          const a = data[i];
          const b = data[j];

          // Only check same side (bullish/bearish)
          if (a.isBullish !== b.isBullish) continue;

          if (this._checkCollision(a, b)) {
            // Move the lower priority marker
            const toMove = a.priority >= b.priority ? b : a;
            const obstacle = a.priority >= b.priority ? a : b;

            const prevDisplacement = toMove.displacementAmount;
            this._displaceMarker(toMove, obstacle);
            if (toMove.displacementAmount !== prevDisplacement) {
              anyDisplaced = true;
            }
          }
        }
      }

      if (!anyDisplaced) break;
    }
  }

  // Check if two markers collide (bounding box intersection)
  private _checkCollision(a: SignalMarkerRendererData, b: SignalMarkerRendererData): boolean {
    const boxPadding = this._boxPadding;
    const minXGap = SignalMarkerPrimitive.MIN_X_GAP;
    const minYGap = SignalMarkerPrimitive.MIN_Y_GAP;

    // X overlap check
    const aLeft = a.x + a.horizontalOffset - a.stackWidth / 2 - boxPadding;
    const aRight = a.x + a.horizontalOffset + a.stackWidth / 2 + boxPadding;
    const bLeft = b.x + b.horizontalOffset - b.stackWidth / 2 - boxPadding;
    const bRight = b.x + b.horizontalOffset + b.stackWidth / 2 + boxPadding;

    if (aRight + minXGap < bLeft || bRight + minXGap < aLeft) return false;

    // Y overlap check
    if (a.isBullish) {
      const aTop = a.y + a.displacementAmount - boxPadding;
      const aBottom = aTop + a.stackHeight + boxPadding * 2;
      const bTop = b.y + b.displacementAmount - boxPadding;
      const bBottom = bTop + b.stackHeight + boxPadding * 2;
      return aTop < bBottom + minYGap && aBottom > bTop - minYGap;
    } else {
      const aBottom = a.y + a.displacementAmount + boxPadding;
      const aTop = aBottom - a.stackHeight - boxPadding * 2;
      const bBottom = b.y + b.displacementAmount + boxPadding;
      const bTop = bBottom - b.stackHeight - boxPadding * 2;
      return aTop < bBottom + minYGap && aBottom > bTop - minYGap;
    }
  }

  // Displace marker away from obstacle
  private _displaceMarker(marker: SignalMarkerRendererData, obstacle: SignalMarkerRendererData): void {
    const boxPadding = this._boxPadding;
    const minYGap = SignalMarkerPrimitive.MIN_Y_GAP;

    if (marker.isBullish) {
      const obstacleBottom = obstacle.y + obstacle.displacementAmount + obstacle.stackHeight + boxPadding * 2;
      const displacement = obstacleBottom + minYGap - marker.y + boxPadding;
      if (displacement > marker.displacementAmount) {
        marker.displacementAmount = displacement;
        marker.isDisplaced = true;
      }
    } else {
      const obstacleTop = obstacle.y + obstacle.displacementAmount - obstacle.stackHeight - boxPadding * 2;
      const displacement = obstacleTop - minYGap - marker.y - marker.stackHeight - boxPadding;
      if (displacement < marker.displacementAmount) {
        marker.displacementAmount = displacement;
        marker.isDisplaced = true;
      }
    }
  }

  // Check and resolve collisions between markers and candle bodies/wicks
  // Only check candles that truly overlap with the marker's horizontal bounds
  private _resolveMarkerCandleCollisions(data: SignalMarkerRendererData[]): void {
    if (this._candleBounds.length === 0) return;

    const minYGap = SignalMarkerPrimitive.MIN_Y_GAP;
    const boxPadding = this._boxPadding;

    for (const marker of data) {
      const markerLeft = marker.x + marker.horizontalOffset - marker.stackWidth / 2 - boxPadding;
      const markerRight = marker.x + marker.horizontalOffset + marker.stackWidth / 2 + boxPadding;

      // Only check candles that actually overlap horizontally with this marker
      for (const candle of this._candleBounds) {
        // Skip the marker's own candle (we already offset from it)
        if (Math.abs(candle.x - marker.anchorX) < candle.width) {
          continue;
        }

        // Check if there's TRUE horizontal overlap (marker bounds intersect candle bounds)
        const candleLeft = candle.x - candle.width / 2;
        const candleRight = candle.x + candle.width / 2;

        // Only process if there's actual horizontal overlap
        if (markerRight <= candleLeft || markerLeft >= candleRight) {
          continue;
        }

        // Calculate marker vertical bounds (including box padding)
        let markerTop: number;
        let markerBottom: number;
        if (marker.isBullish) {
          markerTop = marker.y + marker.displacementAmount - boxPadding;
          markerBottom = markerTop + marker.stackHeight + boxPadding * 2;
        } else {
          markerBottom = marker.y + marker.displacementAmount + boxPadding;
          markerTop = markerBottom - marker.stackHeight - boxPadding * 2;
        }

        // Check collision with candle body only (not wicks - too aggressive)
        if (marker.isBullish) {
          // Bullish marker is below candle - check against candle body bottom
          if (markerTop < candle.bodyBottom + minYGap) {
            const displacement = candle.bodyBottom + minYGap - marker.y;
            if (displacement > marker.displacementAmount) {
              marker.displacementAmount = displacement;
              marker.isDisplaced = true;
            }
          }
        } else {
          // Bearish marker is above candle - check against candle body top
          if (markerBottom > candle.bodyTop - minYGap) {
            const displacement = candle.bodyTop - minYGap - marker.y - marker.stackHeight;
            if (displacement < marker.displacementAmount) {
              marker.displacementAmount = displacement;
              marker.isDisplaced = true;
            }
          }
        }
      }
    }
  }
}
